import React, { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Card,
  CardBody,
  CardHeader,
  CardText,
  Button,
  Input,
  Label,
  Row,
  Col,
  Table,
  Badge,
  Spinner,
} from 'reactstrap'
import QRCode from 'react-qr-code'
import pmlogo from 'assets/images/pmlogo.png'
import { thermalPrinter } from 'utils/thermalPrinter'
import { useDispatch, useSelector } from 'react-redux'
import { getWarehouse } from 'features/public-views/manageStock/API/manageStockAction'
import { setQrPrintingInventoryCache } from 'features/public-views/manageStock/API/manageStockSlice'
import BreadCrumbs from 'components/breadcrumbs'
import PMBSelect from 'components/PMBSelect'
import PMBSearchInputGroup from 'components/PMBSearchInputGroup'
import { TableComponent as DataTable } from 'components/TableComponent'
import {
  Printer,
  Grid,
  List as ListIcon,
  Plus,
  Trash2,
  Search,
} from 'react-feather'
import {
  getItemsInventory,
  getInventory,
} from 'features/public-views/manageStock/API/manageStockAction'
import FormatProcessor from 'utils/FormatProcessor'
import { renderQrWithLogoToCanvas } from 'utils/qrLogoHelper'

const actionOptions = [
  { value: 'createInventory', label: 'Buat Inventori (Create Inventory)' },
  { value: 'searchInventory', label: 'Cari Inventori (Search Inventory)' },
  {
    value: 'createSellTransactionInventory',
    label: 'Transaksi Jual - Inventori Anda',
  },
  {
    value: 'createSellTransactionGeneral',
    label: 'Transaksi Jual - Barang Umum',
  },
]

const QRPrinting = () => {
  const dispatch = useDispatch()
  const { warehouses, itemsInventory, qrPrintingInventoryCache } = useSelector(
    (s) => s.manageStock,
  )

  const [viewMode, setViewMode] = useState('list') // list or preview

  // Builder state
  const [selectedAction, setSelectedAction] = useState(actionOptions[0])
  const [qrList, setQrList] = useState([]) // "Cart" of QRs to print

  // Form states - Create Inventory
  const [createItem, setCreateItem] = useState(null)
  const [createAmount, setCreateAmount] = useState('')
  const [createGudang, setCreateGudang] = useState(null)

  // Form states - Search Inventory
  const [searchInv, setSearchInv] = useState(null)

  // Options loading states (data is cached in Redux)
  const [loadingItems, setLoadingItems] = useState(false)
  const [loadingInv, setLoadingInv] = useState(false)

  const masterItems = useMemo(() => {
    const source = Array.isArray(itemsInventory) ? itemsInventory : []
    return source.map((item) => ({
      value: item.id,
      label: `${item.nameItem || item.name || 'Unknown Item'} (${item.id})`,
      data: item,
    }))
  }, [itemsInventory])

  const inventoryList = useMemo(() => {
    const source = Array.isArray(qrPrintingInventoryCache)
      ? qrPrintingInventoryCache
      : []

    return source
      .filter((inv) => !inv.pending && !inv.komoditasUmum)
      .map((inv) => ({
        value: inv.itemId || inv.id,
        id: inv.id,
        label: `${inv.item?.nameItem || inv.itemName || inv.masterItem?.name || 'Inventori'} - ${inv.amount} ${inv.unit} (ID: ${inv.id})`,
        data: inv,
      }))
  }, [qrPrintingInventoryCache])

  useEffect(() => {
    dispatch(getWarehouse({ isForFilter: true }))

    // SWR for items: show cached options immediately, refresh in background.
    const hasCachedItems =
      Array.isArray(itemsInventory) && itemsInventory.length > 0
    setLoadingItems(!hasCachedItems)
    dispatch(getItemsInventory()).finally(() => setLoadingItems(false))
  }, [dispatch])

  const refreshInventoryForQr = useCallback(() => {
    // SWR for inventory options: use Redux cache immediately, always revalidate.
    const hasCachedInventory =
      Array.isArray(qrPrintingInventoryCache) &&
      qrPrintingInventoryCache.length > 0

    setLoadingInv(!hasCachedInventory)

    dispatch(
      getInventory({
        page: 0,
        size: 500,
        sortBy: 'createdAt',
        asc: 'desc',
        all: true,
      }),
    )
      .then((res) => {
        if (Array.isArray(res?.payload?.items)) {
          dispatch(setQrPrintingInventoryCache(res.payload.items))
        }
      })
      .finally(() => setLoadingInv(false))
  }, [dispatch, qrPrintingInventoryCache?.length])

  // Refresh inventory cache when inventory-based action is selected.
  useEffect(() => {
    if (
      selectedAction.value === 'searchInventory' ||
      selectedAction.value === 'createSellTransactionInventory'
    ) {
      refreshInventoryForQr()
    }
  }, [selectedAction, refreshInventoryForQr])

  const warehouseOptions = useMemo(() => {
    if (!warehouses?.newData) return []
    return warehouses.newData.map((w) => ({
      value: w.id,
      label: w.name,
    }))
  }, [warehouses])

  const buildQrValue = useCallback((command, payload) => {
    const productDescription =
      payload.productDescription || payload.nameItem || payload.name || ''

    return FormatProcessor.generate(command, {
      ...payload,
      productDescription,
    })
  }, [])

  const handleAddToList = () => {
    if (selectedAction.value === 'createInventory') {
      if (!createItem) return

      const payload = {
        itemId: createItem.value,
        nameItem:
          createItem.data?.nameItem ||
          createItem.data?.name ||
          createItem.label,
        amount: createAmount !== '' ? Number(createAmount) : null,
        gtin: createItem.data?.gtin || '',
        expiry: createItem.data?.expiry || '',
        traceCode: createItem.data?.traceCode || '',
      }
      if (createGudang) {
        payload.gudang = createGudang.value
      }

      setQrList((prev) => [
        ...prev,
        {
          id: Date.now(),
          action: 'createInventory',
          actionLabel: 'Buat Inventori',
          displayTitle: `${createItem.data?.nameItem || createItem.data?.name || createItem.label}`,
          displaySubtitle: `Qty: ${createAmount || 'Isi setelah scan'} | Gudang: ${createGudang ? createGudang.label : '-'} | GTIN: ${createItem.data?.gtin || '-'} | Exp: ${createItem.data?.expiry || '-'}`,
          qrValue: buildQrValue('createInventory', {
            ...payload,
            productDescription:
              createItem.data?.nameItem ||
              createItem.data?.name ||
              createItem.label,
          }),
        },
      ])

      // reset
      setCreateItem(null)
      setCreateAmount('')
      setCreateGudang(null)
    }

    if (selectedAction.value === 'searchInventory') {
      if (!searchInv) return

      const payload = {
        id: searchInv.value,
        name:
          searchInv.data?.item?.nameItem ||
          searchInv.data?.itemName ||
          searchInv.data?.masterItem?.name ||
          searchInv.value,
        gtin: searchInv.data?.gtin || '',
        expiry: searchInv.data?.expiry || '',
        traceCode: searchInv.data?.traceCode || '',
        groupId: searchInv.data?.groupId || searchInv.data?.group?.id || null,
      }

      setQrList((prev) => [
        ...prev,
        {
          id: Date.now(),
          action: 'searchInventory',
          actionLabel: 'Cari Inventori',
          displayTitle: `ID: ${searchInv.value}`,
          displaySubtitle: `Nama: ${payload.name} | GTIN: ${payload.gtin || '-'} | Exp: ${payload.expiry || '-'}`,
          qrValue: buildQrValue('searchInventory', {
            ...payload,
            productDescription: payload.name,
          }),
        },
      ])

      // reset
      setSearchInv(null)
    }

    if (selectedAction.value === 'createSellTransactionInventory') {
      if (!searchInv) return

      const payload = {
        itemId: searchInv.data?.itemId,
        amount: createAmount !== '' ? Number(createAmount) : null,
        inventoryId: searchInv.id,
        gtin: searchInv.data?.gtin || '',
        expiry: searchInv.data?.expiry || '',
        traceCode: searchInv.data?.traceCode || '',
        groupId: searchInv.data?.groupId || searchInv.data?.group?.id || null,
        nameItem:
          searchInv.data?.item?.nameItem ||
          searchInv.data?.itemName ||
          searchInv.label,
      }

      setQrList((prev) => [
        ...prev,
        {
          id: Date.now(),
          action: 'createSellTransactionInventory',
          actionLabel: 'Jual (Inventori)',
          displayTitle: `${payload.nameItem}`,
          displaySubtitle: `ID: ${searchInv.id} | Qty: ${createAmount || 'Isi setelah scan'} | GTIN: ${payload.gtin || '-'} | Exp: ${payload.expiry || '-'}`,
          qrValue: buildQrValue('createSellTransaction', {
              ...payload,
              productDescription: payload.nameItem,
            },
          ),
        },
      ])
      setSearchInv(null)
      setCreateAmount('')
    }

    if (selectedAction.value === 'createSellTransactionGeneral') {
      if (!createItem) return

      const payload = {
        itemId: createItem.value,
        nameItem:
          createItem.data?.nameItem ||
          createItem.data?.name ||
          createItem.label,
        amount: createAmount !== '' ? Number(createAmount) : null,
        gtin: createItem.data?.gtin || '',
        expiry: createItem.data?.expiry || '',
        traceCode: createItem.data?.traceCode || '',
      }

      setQrList((prev) => [
        ...prev,
        {
          id: Date.now(),
          action: 'createSellTransactionGeneral',
          actionLabel: 'Jual (Umum)',
          displayTitle: `${payload.nameItem}`,
          displaySubtitle: `Qty: ${createAmount || 'Isi setelah scan'} | GTIN: ${payload.gtin || '-'} | Exp: ${payload.expiry || '-'}`,
          qrValue: buildQrValue('createSellTransaction', {
              ...payload,
              productDescription: payload.nameItem,
            },
          ),
        },
      ])
      setCreateItem(null)
      setCreateAmount('')
    }
  }

  const handleAddAllInventory = () => {
    if (selectedAction.value === 'createInventory') {
      if (masterItems.length === 0) return
      const newItems = masterItems.map((item, idx) => {
        const payload = {
          itemId: item.value,
          nameItem: item.data?.nameItem || item.data?.name || item.label,
          amount: null,
          gtin: item.data?.gtin || '',
          expiry: item.data?.expiry || '',
          traceCode: item.data?.traceCode || '',
        }
        return {
          id: Date.now() + idx,
          action: 'createInventory',
          actionLabel: 'Buat Inventori',
          displayTitle: `${item.data?.nameItem || item.data?.name || item.label}`,
          displaySubtitle: `Qty: Isi setelah scan | Gudang: - | GTIN: ${payload.gtin || '-'} | Exp: ${payload.expiry || '-'}`,
          qrValue: buildQrValue('createInventory', {
            ...payload,
            productDescription:
              item.data?.nameItem || item.data?.name || item.label,
          }),
        }
      })
      setQrList(newItems)
      return
    }

    if (selectedAction.value === 'searchInventory') {
      if (inventoryList.length === 0) return
      const newItems = inventoryList.map((inv, idx) => {
        const payload = {
          id: inv.value,
          name:
            inv.data?.item?.nameItem ||
            inv.data?.itemName ||
            inv.data?.masterItem?.name ||
            inv.value,
          gtin: inv.data?.gtin || '',
          expiry: inv.data?.expiry || '',
          traceCode: inv.data?.traceCode || '',
        }
        return {
          id: Date.now() + idx,
          action: 'searchInventory',
          actionLabel: 'Cari Inventori',
          displayTitle: `ID: ${inv.value}`,
          displaySubtitle: `Nama: ${payload.name} | GTIN: ${payload.gtin || '-'} | Exp: ${payload.expiry || '-'}`,
          qrValue: buildQrValue('searchInventory', {
            ...payload,
            productDescription: payload.name,
          }),
        }
      })
      setQrList(newItems)
      return
    }

    if (selectedAction.value === 'createSellTransactionInventory') {
      const newItems = inventoryList.map((inv) => {
        const payload = {
          itemId: inv.data?.itemId,
          amount: null,
          inventoryId: inv.id,
          gtin: inv.data?.gtin || '',
          expiry: inv.data?.expiry || '',
          traceCode: inv.data?.traceCode || '',
          groupId: inv.data?.groupId || inv.data?.group?.id || null,
          nameItem: inv.data?.item?.nameItem || inv.data?.itemName || inv.label,
        }
        return {
          id: Date.now() + Math.random(),
          action: 'createSellTransactionInventory',
          actionLabel: 'Jual (Inventori)',
          displayTitle: `${payload.nameItem}`,
          displaySubtitle: `ID: ${inv.id} | Qty: Isi setelah scan | GTIN: ${payload.gtin || '-'} | Exp: ${payload.expiry || '-'}`,
          qrValue: buildQrValue('createSellTransaction', {
              ...payload,
              productDescription: payload.nameItem,
            },
          ),
        }
      })
      setQrList(newItems)
    }

    if (selectedAction.value === 'createSellTransactionGeneral') {
      const newItems = masterItems.map((item) => {
        const payload = {
          itemId: item.value,
          nameItem: item.data?.nameItem || item.data?.name || item.label,
          amount: null,
          gtin: item.data?.gtin || '',
          expiry: item.data?.expiry || '',
          traceCode: item.data?.traceCode || '',
        }
        return {
          id: Date.now() + Math.random(),
          action: 'createSellTransactionGeneral',
          actionLabel: 'Jual (Umum)',
          displayTitle: `${payload.nameItem}`,
          displaySubtitle: `Qty: Isi setelah scan | GTIN: ${payload.gtin || '-'} | Exp: ${payload.expiry || '-'}`,
          qrValue: buildQrValue('createSellTransaction', {
            ...payload,
            productDescription: payload.nameItem,
          }),
        }
      })
      setQrList(newItems)
    }
  }

  const handleRemoveFromList = (id) => {
    setQrList((prev) => prev.filter((item) => item.id !== id))
  }

  // Derived properties for UI validation
  const isCreateValid = !!createItem
  const isSearchValid = !!searchInv

  const [isConnected, setIsConnected] = useState(thermalPrinter.isConnected)
  const [isConnecting, setIsConnecting] = useState(false)
  const [isThermalPrinting, setIsThermalPrinting] = useState(false)

  const handleToggleConnect = async () => {
    if (isConnected) {
      await thermalPrinter.disconnect()
      setIsConnected(false)
    } else {
      setIsConnecting(true)
      try {
        const success = await thermalPrinter.connect()
        if (success) setIsConnected(true)
      } catch (err) {
        alert('Gagal menghubungkan printer.')
      } finally {
        setIsConnecting(false)
      }
    }
  }

  useEffect(() => {
    const tryAutoConnect = async () => {
      setIsConnecting(true)
      const success = await thermalPrinter.autoConnect()
      setIsConnecting(false)
      if (success) {
        setIsConnected(true)
      }
    }
    tryAutoConnect()
  }, [])

  const handleThermalPrint = async () => {
    if (!navigator.bluetooth) {
      alert('Browser Anda tidak mendukung fitur Bluetooth.')
      return
    }

    if (!thermalPrinter.isConnected) {
      alert('Printer belum terhubung. Silakan hubungkan di menu Pengaturan.')
      return
    }

    setIsThermalPrinting(true)
    try {
      // Find all QR card elements
      const qrCards = document.querySelectorAll('.qr-card')

      for (let i = 0; i < qrList.length; i++) {
        const item = qrList[i]
        const cardRef = qrCards[i]

        if (!cardRef) continue

        // 1. Initialize and set layout
        await thermalPrinter.init()
        await thermalPrinter.center()

        // 2. Print Labels (Title & Subtitle) as Text for clarity
        await thermalPrinter.write(new Uint8Array([0x1b, 0x45, 0x01])) // Bold ON
        await thermalPrinter.text(item.displayTitle.toUpperCase())
        await thermalPrinter.write(new Uint8Array([0x1b, 0x45, 0x00])) // Bold OFF
        await thermalPrinter.text(item.displaySubtitle)
        await thermalPrinter.feed(1)

        // 3. Render QR with Logo to Canvas and print as Image
        const svg = cardRef.querySelector('svg')
        const logoImg = cardRef.querySelector('img')
        const canvas = await renderQrWithLogoToCanvas(svg, logoImg, 384)
        
        if (canvas) {
          await thermalPrinter.printImage(canvas)
        } else {
          // Fallback if canvas render fails
          await thermalPrinter.printQR(item.qrValue)
        }

        // 4. Feed/Cutter
        await thermalPrinter.feed(4)
      }
    } catch (err) {
      console.error(err)
      alert('Gagal mencetak ke Bluetooth terminal.')
    } finally {
      setIsThermalPrinting(false)
    }
  }


  const [cartSearch, setCartSearch] = useState('')
  const [cartActionFilter, setCartActionFilter] = useState({
    value: 'all',
    label: 'Semua Action',
  })

  const cartFilterOptions = useMemo(
    () => [
      { value: 'all', label: 'Semua Action' },
      ...actionOptions.map((opt) => ({
        value: opt.value,
        label: opt.label.split(' (')[0],
      })),
    ],
    [],
  )

  const filteredQrList = useMemo(() => {
    let list = qrList

    if (cartActionFilter.value !== 'all') {
      list = list.filter((item) => item.action === cartActionFilter.value)
    }

    if (cartSearch) {
      list = list.filter(
        (item) =>
          item.displayTitle.toLowerCase().includes(cartSearch.toLowerCase()) ||
          item.displaySubtitle
            .toLowerCase()
            .includes(cartSearch.toLowerCase()) ||
          item.actionLabel.toLowerCase().includes(cartSearch.toLowerCase()),
      )
    }

    return list
  }, [qrList, cartSearch, cartActionFilter])

  const isSellAction = (action) =>
    action === 'createSellTransactionInventory' ||
    action === 'createSellTransactionGeneral'

  const columns = useMemo(
    () => [
      {
        name: 'Action',
        minWidth: '150px',
        cell: (row) => (
          <Badge
            color={
              row.action === 'createInventory'
                ? 'primary'
                : isSellAction(row.action)
                  ? 'warning'
                  : 'success'
            }
          >
            {row.actionLabel}
          </Badge>
        ),
      },
      {
        name: 'Inventori',
        minWidth: '200px',
        cell: (row) => <span className="fw-bold">{row.displayTitle}</span>,
      },
      {
        name: 'Data Tambahan',
        minWidth: '250px',
        cell: (row) => (
          <span className="text-muted small">{row.displaySubtitle}</span>
        ),
      },
      {
        name: '',
        width: '80px',
        center: true,
        cell: (row) => (
          <Button
            color="danger"
            outline
            className="p-1"
            onClick={() => handleRemoveFromList(row.id)}
            data-testid={`qr-print-list-row-${row?.id}`}
          >
            <Trash2 size={14} />
          </Button>
        ),
      },
    ],
    [handleRemoveFromList],
  )

  return (
    <div className="w-100 px-3 pb-5">
      <div className="mt-3 no-print">
        <BreadCrumbs breadCrumbActive="Print QR" />
      </div>

      <Card className="border-0 shadow-sm mb-4 pt-2">
        <CardHeader className="bg-transparent border-bottom-0 pb-0 d-flex justify-content-between align-items-center flex-wrap gap-2 no-print">
          <div>
            <CardText tag="h5" className="fw-semibold mb-0">
              Print QR
            </CardText>
            <small className="text-muted">
              Pilih tujuan (action) dan isi formulir untuk menambahkan QR Code
              ke daftar print.
            </small>
          </div>
          <div className="d-flex gap-2">
            {viewMode === 'preview' ? (
              <Button
                color={'outline-primary'}
                onClick={() => setViewMode('list')}
                data-testid="qr-print-back-button"
                // size="sm"
              >
                Kembali
              </Button>
            ) : (
              <Button
                color={'primary'}
                onClick={() => setViewMode('preview')}
                disabled={qrList.length === 0}
                data-testid="qr-print-preview-button"
              >
                Preview &amp; Print
              </Button>
            )}
          </div>
        </CardHeader>
        <CardBody className="pt-4 print-section">
          {viewMode === 'list' && (
            <Row>
              <Col md={5} className="border-end">
                <h6 className="fw-semibold mb-3">Formulir QR</h6>

                <Label>Pilih Action</Label>
                <div className="mb-3">
                  <PMBSelect
                    options={actionOptions}
                    value={selectedAction}
                    onChange={(val) => setSelectedAction(val)}
                    testId="qr-print-action-select"
                  />
                </div>

                <div className="p-3 bg-light rounded border">
                  <Label className="fw-bold text-primary mb-3 d-block border-bottom pb-2">
                    Detail Input
                  </Label>

                  {(selectedAction.value === 'createInventory' ||
                    selectedAction.value ===
                      'createSellTransactionGeneral') && (
                    <div className="mb-3">
                      <Label>Cari & Pilih Item</Label>
                      <PMBSelect
                        placeholder="Ketik untuk mencari nama item..."
                        options={masterItems}
                        isLoading={loadingItems}
                        value={createItem}
                        onChange={setCreateItem}
                        testId="qr-print-item-select"
                      />
                    </div>
                  )}

                  {(selectedAction.value === 'searchInventory' ||
                    selectedAction.value ===
                      'createSellTransactionInventory') && (
                    <div className="mb-3">
                      <Label>Cari & Pilih Inventori *</Label>
                      <PMBSelect
                        placeholder="Ketik untuk mencari nama inventori..."
                        options={inventoryList}
                        isLoading={loadingInv}
                        value={searchInv}
                        onChange={(val) => {
                          setSearchInv(val)
                          if (
                            selectedAction.value ===
                              'createSellTransactionInventory' &&
                            val
                          ) {
                            setCreateAmount(val.data?.amount ?? '')
                          }
                        }}
                        testId="qr-print-item-select"
                      />
                    </div>
                  )}

                  {(selectedAction.value === 'createInventory' ||
                    selectedAction.value === 'createSellTransactionInventory' ||
                    selectedAction.value ===
                      'createSellTransactionGeneral') && (
                    <div className="mb-3">
                      <Label>Berat (Optional)</Label>
                      <Input
                        type="number"
                        placeholder="Contoh: 100"
                        value={createAmount}
                        disabled={
                          (selectedAction.value ===
                            'createSellTransactionInventory' &&
                            !searchInv) ||
                          ((selectedAction.value === 'createInventory' ||
                            selectedAction.value ===
                              'createSellTransactionGeneral') &&
                            !createItem)
                        }
                        max={
                          selectedAction.value ===
                            'createSellTransactionInventory' && searchInv
                            ? searchInv.data?.amount
                            : undefined
                        }
                        data-testid="qr-print-amount-input"
                        onChange={(e) => {
                          let val = e.target.value
                          if (
                            selectedAction.value ===
                              'createSellTransactionInventory' &&
                            searchInv &&
                            val !== '' &&
                            Number(val) > Number(searchInv.data?.amount)
                          ) {
                            val = searchInv.data?.amount
                          }
                          setCreateAmount(val)
                        }}
                      />
                      <small className="text-muted">
                        Kosongkan jika admin lapangan akan menginput berat
                        setelah scan.
                      </small>
                    </div>
                  )}

                  {selectedAction.value === 'createInventory' && (
                    <div className="mb-3">
                      <Label>Pilih Gudang (Optional)</Label>
                      <PMBSelect
                        placeholder="Pilih lokasi penyimpanan..."
                        options={warehouseOptions}
                        value={createGudang}
                        onChange={setCreateGudang}
                        isClearable
                      />
                    </div>
                  )}

                  <div className="d-flex gap-2 mt-4">
                    <Button
                      color="primary"
                      className="flex-grow-1"
                      onClick={handleAddToList}
                      disabled={
                        selectedAction.value === 'createInventory' ||
                        selectedAction.value === 'createSellTransactionGeneral'
                          ? !isCreateValid
                          : !isSearchValid
                      }
                    >
                      <Plus size={16} className="me-2" />
                      Tambah
                    </Button>
                    <Button
                      color="outline-primary"
                      onClick={handleAddAllInventory}
                      disabled={
                        selectedAction.value === 'createInventory' ||
                        selectedAction.value === 'createSellTransactionGeneral'
                          ? loadingItems || masterItems.length === 0
                          : loadingInv || inventoryList.length === 0
                      }
                    >
                      Pilih Semua Inventori
                    </Button>
                  </div>
                </div>
              </Col>

              <Col md={7}>
                <div className="d-flex justify-content-between align-items-center mb-3 mt-4 mt-md-0 gap-2 flex-wrap">
                  <div className="d-flex gap-2 flex-grow-1">
                    <div className="flex-grow-1" style={{ maxWidth: '300px' }}>
                      <PMBSearchInputGroup
                        placeholder="Cari di daftar..."
                        value={cartSearch}
                        onChange={(e) => setCartSearch(e.target.value)}
                        maxWidth="100%"
                      />
                    </div>
                    <div style={{ width: '180px' }}>
                      <PMBSelect
                        options={cartFilterOptions}
                        value={cartActionFilter}
                        onChange={(val) => setCartActionFilter(val)}
                      />
                    </div>
                  </div>
                  {qrList.length > 0 && (
                    <Button
                      color="outline-danger"
                      onClick={() => setQrList([])}
                    >
                      Hapus semua
                    </Button>
                  )}
                </div>

                {qrList.length === 0 ? (
                  <div className="text-center py-5 bg-light rounded border">
                    <Grid size={40} className="text-muted mb-3" />
                    <p className="text-muted mb-0">
                      Belum ada QR Code yang ditambahkan.
                      <br />
                      Silakan buat QR Code melalui formulir di samping.
                    </p>
                  </div>
                ) : filteredQrList.length === 0 ? (
                  <div className="text-center py-5 bg-light rounded border">
                    <p className="text-muted mb-0">
                      Tidak ada QR yang cocok dengan pencarian "{cartSearch}".
                    </p>
                  </div>
                ) : (
                  <DataTable
                    columns={columns}
                    data={filteredQrList}
                    noDataComponent={
                      <div className="p-3 text-muted">Tidak ada data.</div>
                    }
                    fixedHeader
                    fixedHeaderScrollHeight="calc(100vh - 380px)"
                  />
                )}

                {/* {qrList.length > 0 && (
                  <div className="mt-4 pt-3 border-top text-end">
                    <Button
                      color="primary"
                      onClick={() => setViewMode('preview')}
                      // size="lg"
                    >
                      <Printer size={16} className="me-2" />
                      Preview &amp; Print Sekarang
                    </Button>
                  </div>
                )} */}
              </Col>
            </Row>
          )}

          {viewMode === 'preview' && (
            <div className="mt-2">
              <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom no-print">
                <h6 className="fw-semibold mb-0">
                  Preview {qrList.length} Lembar QR Code
                </h6>
                <div className="d-flex gap-2">
                  <Button
                    color={isConnected ? 'primary' : 'warning'}
                    // size="sm"
                    onClick={
                      isConnected ? handleThermalPrint : handleToggleConnect
                    }
                    disabled={isThermalPrinting || isConnecting}
                  >
                    {isThermalPrinting || isConnecting ? (
                      <Spinner size="sm" />
                    ) : (
                      <Printer size={14} className="me-2" />
                    )}
                    {isConnected
                      ? 'Print Bluetooth (58mm)'
                      : 'Hubungkan Printer'}
                  </Button>
                  {isConnected && (
                    <Button
                      color="danger"
                      outline
                      // size="sm"
                      onClick={handleToggleConnect}
                      // title="Putuskan koneksi printer"
                    >
                      Putuskan koneksi printer
                    </Button>
                  )}
                  {/* <Button
                    color="primary"
                    outline
                    size="sm"
                    onClick={() => window.print()}
                  >
                    <Printer size={14} className="me-2" />
                    Print Halaman (Standard)
                  </Button> */}
                </div>
              </div>

              <div className="d-flex flex-wrap gap-3 justify-content-center pb-5">
                {qrList.map((item) => {
                  return (
                    <div
                      key={item.id}
                      className="bg-white p-3 rounded shadow-sm border text-center d-flex flex-column align-items-center qr-card"
                      style={{ width: '230px' }}
                    >
                      <div
                        style={{
                          position: 'relative',
                          display: 'inline-block',
                        }}
                      >
                        <QRCode
                          value={item.qrValue}
                          size={180}
                          level="H"
                          style={{
                            shapeRendering: 'crispEdges',
                          }}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: 'white',
                            padding: '5px',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <img
                            src={pmlogo}
                            alt="logo"
                            style={{
                              width: '32px',
                              height: '32px',
                              imageRendering: 'pixelated',
                            }}
                          />
                        </div>
                      </div>
                      <div className="mt-3 w-100">
                        <Badge
                          color={
                            item.action === 'createInventory'
                              ? 'light-primary'
                              : isSellAction(item.action)
                                ? 'light-warning'
                                : 'light-success'
                          }
                          className="mb-2"
                        >
                          {item.actionLabel}
                        </Badge>
                        <div
                          className="fw-bold mb-1 text-truncate"
                          title={item.displayTitle}
                        >
                          {item.displayTitle}
                        </div>
                        <small
                          className="text-muted d-block text-truncate"
                          title={item.displaySubtitle}
                        >
                          {item.displaySubtitle}
                        </small>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Basic print styles */}
      <style>{`
        @page {
          size: auto;
          margin: 0mm;
        }
        @media print {
          /* Standard visibility toggle to avoid display:none inheritance issues */
          body * {
            visibility: hidden;
            margin: 0 !important;
          }
          .print-section, .print-section * {
            visibility: visible;
          }
          /* Absolute removal of labels the user wants gone */
          .no-print, .no-print * {
            display: none !important;
          }
          .print-section {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          /* Ensure the grid is exactly 3 columns and centered */
          .d-flex.flex-wrap {
            display: grid !important;
            grid-template-columns: repeat(3, 230px) !important;
            justify-content: center !important;
            gap: 15px !important;
            width: 100% !important;
          }
        }
      `}</style>
    </div>
  )
}

export default QRPrinting
